import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { inventoryItemsApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';

interface CreateItemForm {
  itemCode: string; name: string; description?: string; categoryId?: number; unitOfMeasureId?: number;
  reorderLevel?: number; minimumStock?: number; trackBatch?: boolean; trackExpiry?: boolean; trackSerialNumber?: boolean; isAssetCreating?: boolean;
}

export function ItemCataloguePage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateItemForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['inventory-items'], queryFn: () => inventoryItemsApi.list() });
  const { data: categoriesData } = useQuery({ queryKey: ['inventory-categories'], queryFn: () => inventoryItemsApi.listCategories() });
  const categories = categoriesData?.data ?? [];
  const { data: unitsData } = useQuery({ queryKey: ['units-of-measure'], queryFn: () => inventoryItemsApi.listUnitsOfMeasure() });
  const units = unitsData?.data ?? [];
  const categoriesById = new Map(categories.map((c) => [c.id, c]));
  const unitsById = new Map(units.map((u) => [u.id, u]));

  const createMutation = useMutation({
    mutationFn: (input: CreateItemForm) => inventoryItemsApi.create({
      ...input, categoryId: input.categoryId ? Number(input.categoryId) : undefined, unitOfMeasureId: input.unitOfMeasureId ? Number(input.unitOfMeasureId) : undefined,
      unitOfMeasure: unitsById.get(Number(input.unitOfMeasureId))?.name ?? 'Piece', // backend still requires the free-text unitOfMeasure field alongside the new FK
      reorderLevel: input.reorderLevel ? Number(input.reorderLevel) : undefined, minimumStock: input.minimumStock ? Number(input.minimumStock) : undefined,
    }),
    onSuccess: (result) => {
      showSuccess(`Item ${result.data.itemCode} added to the catalogue.`);
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      setShowCreateForm(false);
      reset();
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const items = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Item Catalogue" description="The central list of stock items, with their tracking rules and category." />

      <SectionHeader title="Items" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'Add Item'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Item Code <input {...register('itemCode', { required: true })} /></label>
          <label>Name <input {...register('name', { required: true })} /></label>
          <label>Category
            <select {...register('categoryId', { valueAsNumber: true })}>
              <option value="">Select…</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Unit of Measure
            <select {...register('unitOfMeasureId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <label>Reorder Level <input type="number" step="0.01" {...register('reorderLevel')} /></label>
          <label>Minimum Stock <input type="number" step="0.01" {...register('minimumStock')} /></label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><input type="checkbox" {...register('trackBatch')} /> Track Batch/Lot</label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><input type="checkbox" {...register('trackExpiry')} /> Track Expiry</label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><input type="checkbox" {...register('trackSerialNumber')} /> Track Serial Number</label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><input type="checkbox" {...register('isAssetCreating')} /> Asset-Creating Item</label>
          <button type="submit" disabled={createMutation.isPending}>Add to Catalogue</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load the item catalogue. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={items}
          rowKey={(i) => i.id}
          columns={[
            { header: 'Item Code', render: (i) => i.item_code },
            { header: 'Name', render: (i) => i.name },
            { header: 'Category', render: (i) => (i.category_id ? categoriesById.get(i.category_id)?.name : i.category) ?? '-' },
            { header: 'Unit', render: (i) => i.unit_of_measure },
            { header: 'Reorder Level', render: (i) => i.reorder_level ?? '-' },
            {
              header: 'Tracking', render: (i) => [
                i.track_batch ? 'Batch' : null, i.track_expiry ? 'Expiry' : null, i.is_asset_creating ? 'Asset-Creating' : null,
              ].filter(Boolean).join(', ') || '-',
            },
            { header: 'Status', render: (i) => <StatusBadge status={i.is_active ? 'active' : 'inactive'} /> },
          ]}
        />
      )}
    </div>
  );
}
